// Invoice edit — Wave D.
//
// Server's `PUT /invoices/:enc_id` accepts only `status`, `notes`, `due_date`
// (per `updateInvoiceSchema`). Line items are immutable post-create. The
// edit form therefore exposes only those three fields, plus links back to
// the detail page on save.

import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader/PageHeader';
import { Card } from '../../components/ui/Card/Card';
import { Button } from '../../components/ui/Button/Button';
import { Select } from '../../components/form/Select';
import { Textarea } from '../../components/form/Textarea';
import { DatePicker } from '../../components/form/DatePicker';
import { Spinner } from '../../components/ui/Spinner/Spinner';
import { useInvoiceDetail, useUpdateInvoice } from './hooks/useInvoices';
import type { InvoiceUserSettableStatus } from '../../types/api';
import styles from './InvoiceEditPage.module.css';

export function InvoiceEditPage(): JSX.Element {
  const { enc_id: encIdParam, id: idParam } = useParams<{ enc_id?: string; id?: string }>();
  const encId = encIdParam ?? idParam;
  const navigate = useNavigate();
  const detail = useInvoiceDetail(encId);
  const update = useUpdateInvoice(encId ?? '');

  const [status, setStatus] = useState<InvoiceUserSettableStatus>('issued');
  const [dueDate, setDueDate] = useState<string>('');
  const [notes, setNotes] = useState<string>('');

  useEffect(() => {
    const inv = detail.data?.invoice;
    if (!inv) return;
    if (inv.status === 'issued' || inv.status === 'paid' || inv.status === 'cancelled') {
      setStatus(inv.status);
    } else {
      setStatus('issued');
    }
    setDueDate(inv.due_date ?? '');
    setNotes(inv.notes ?? '');
  }, [detail.data?.invoice]);

  if (detail.isLoading) {
    return (
      <div className={styles.page}>
        <PageHeader title="Edit invoice" />
        <div className={styles.loading}>
          <Spinner />
        </div>
      </div>
    );
  }

  const invoice = detail.data?.invoice;
  if (!invoice || !encId) {
    return (
      <div className={styles.page}>
        <PageHeader title="Invoice not found" />
        <Card>
          <p>The invoice you requested could not be loaded.</p>
          <Button variant="ghost" icon={ArrowLeft} onClick={() => navigate('/collections/invoices')}>
            Back
          </Button>
        </Card>
      </div>
    );
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    update.mutate(
      {
        status,
        due_date: dueDate || null,
        notes: notes.trim() ? notes : null,
      },
      {
        onSuccess: () => {
          navigate(`/collections/invoices/${encodeURIComponent(encId as string)}`);
        },
      },
    );
  }

  return (
    <form className={styles.page} onSubmit={handleSubmit}>
      <PageHeader
        title={`Edit ${invoice.invoice_number}`}
        subtitle="Update payment state, due date, and notes."
        actions={
          <Button
            variant="ghost"
            icon={ArrowLeft}
            type="button"
            onClick={() => navigate(`/collections/invoices/${encodeURIComponent(invoice.enc_id)}`)}
          >
            Back
          </Button>
        }
      />

      <Card title="Status">
        <Select
          aria-label="Status"
          value={status}
          onChange={(e) => setStatus(e.target.value as InvoiceUserSettableStatus)}
          options={[
            { value: 'issued', label: 'Issued' },
            { value: 'paid', label: 'Paid' },
            { value: 'cancelled', label: 'Cancelled' },
          ]}
        />
        <p className={styles.hint}>
          Server enforces the legal transitions (issued → paid / cancelled, etc.). `overdue`
          is set automatically by the cron worker.
        </p>
      </Card>

      <Card title="Due date">
        <DatePicker
          aria-label="Due date"
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
        />
      </Card>

      <Card title="Notes">
        <Textarea
          aria-label="Notes"
          rows={5}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </Card>

      <div className={styles.footer}>
        <Button
          variant="ghost"
          type="button"
          onClick={() => navigate(`/collections/invoices/${encodeURIComponent(invoice.enc_id)}`)}
        >
          Cancel
        </Button>
        <Button variant="primary" type="submit" loading={update.isPending}>
          Save changes
        </Button>
      </div>
    </form>
  );
}
